import { Flex, Select, SettingsSection, TagBadge, Text, type SelectOption } from '@chatsdet/ui';

export type EnvType = 'permanent' | 'ephemeral' | 'local' | 'sandbox';
export type EnvConstraints =
  | { mode: 'only'; environmentIds: string[] }
  | { mode: 'envTypes'; types: EnvType[] }
  | null;

export interface WorkflowEnvironment {
  id: string;
  name: string;
  type: EnvType;
  isDefault: boolean;
}

export interface WorkflowEnvironmentPassHistory {
  environmentId: string;
  environmentName: string;
  environmentType: EnvType;
  everPassed: boolean;
  passed: number;
  total: number;
  health: string;
}

interface WorkflowEnvironmentSettingsProps {
  constraintMode: 'any' | 'envTypes' | 'only';
  envTypeSelection: EnvType[];
  currentEnvironmentConstraints: EnvConstraints;
  eligibleEnvironmentIds: string[] | null;
  environments: WorkflowEnvironment[] | null;
  defaultEnvironmentId: string | null;
  passByEnvironment: WorkflowEnvironmentPassHistory[] | null;
  onConstraintModeChange: (mode: 'any' | 'envTypes') => void;
  onEnvTypeSelectionChange: (types: EnvType[]) => void;
  onDefaultEnvironmentIdChange: (environmentId: string | null) => void;
}

const ENV_TYPE_OPTIONS: EnvType[] = ['permanent', 'ephemeral', 'local', 'sandbox'];

function buildDefaultEnvironmentOptions(
  environments: WorkflowEnvironment[] | null,
  eligibleEnvironmentIds: string[] | null
): SelectOption[] {
  return [
    { id: '__default__', label: 'Property default' },
    ...(environments ?? [])
      .filter(
        (environment) =>
          environment.type !== 'ephemeral' &&
          (!eligibleEnvironmentIds || eligibleEnvironmentIds.includes(environment.id))
      )
      .map((environment) => ({
        id: environment.id,
        label: `${environment.name} (${environment.type})`,
      })),
  ];
}

function formatPassHistoryLabel(passHistory: WorkflowEnvironmentPassHistory): string {
  const allGreen =
    passHistory.everPassed && passHistory.total > 0 && passHistory.passed === passHistory.total;

  if (passHistory.total === 0) return 'never run';
  if (allGreen) return `✓ passing (${passHistory.passed}/${passHistory.total})`;
  return `⚠ ${passHistory.passed}/${passHistory.total}`;
}

export function WorkflowEnvironmentSettings({
  constraintMode,
  envTypeSelection,
  currentEnvironmentConstraints,
  eligibleEnvironmentIds,
  environments,
  defaultEnvironmentId,
  passByEnvironment,
  onConstraintModeChange,
  onEnvTypeSelectionChange,
  onDefaultEnvironmentIdChange,
}: WorkflowEnvironmentSettingsProps) {
  const defaultEnvironmentOptions = buildDefaultEnvironmentOptions(
    environments,
    eligibleEnvironmentIds
  );

  const toggleEnvType = (envType: EnvType, checked: boolean) => {
    onEnvTypeSelectionChange(
      checked ? [...envTypeSelection, envType] : envTypeSelection.filter((type) => type !== envType)
    );
  };

  return (
    <SettingsSection
      title="Environment"
      description="Which environments this workflow may run against."
    >
      <div className="space-y-2">
        <label className="text-sm font-medium leading-none">Environment eligibility</label>
        <Text size="xs" className="text-muted-foreground">
          Controls which environments this workflow is allowed to run against when a suite targets a
          specific env.
        </Text>
        <div className="space-y-2 rounded-md border border-border bg-card p-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="env-constraint-mode"
              checked={constraintMode === 'any'}
              onChange={() => onConstraintModeChange('any')}
              className="mt-1"
            />
            <div className="flex flex-col">
              <Text size="sm" weight="medium">
                Any environment
              </Text>
              <Text size="xs" className="text-muted-foreground">
                Default — workflow can run anywhere the caller points it.
              </Text>
            </div>
          </label>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="env-constraint-mode"
              checked={constraintMode === 'envTypes'}
              onChange={() => onConstraintModeChange('envTypes')}
              className="mt-1"
            />
            <div className="flex flex-col flex-1">
              <Text size="sm" weight="medium">
                Limit to environment type(s)
              </Text>
              <Text size="xs" className="text-muted-foreground">
                Restrict this workflow to envs of these types.
              </Text>
              {constraintMode === 'envTypes' && (
                <div className="flex gap-4 mt-2">
                  {ENV_TYPE_OPTIONS.map((envType) => (
                    <label key={envType} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={envTypeSelection.includes(envType)}
                        onChange={(event) => toggleEnvType(envType, event.target.checked)}
                      />
                      <Text size="sm">{envType}</Text>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </label>

          {constraintMode === 'only' && (
            <div className="rounded-md bg-muted/50 p-2">
              <Text size="xs" className="text-muted-foreground">
                This workflow is pinned to specific environments. The "only" picker isn't editable
                from this dialog yet — switch to one of the options above to clear, or manage pins
                via the API / CLI.
              </Text>
              {currentEnvironmentConstraints?.mode === 'only' && (
                <Text size="xs" className="font-mono mt-1">
                  {currentEnvironmentConstraints.environmentIds.length} env id
                  {currentEnvironmentConstraints.environmentIds.length === 1 ? '' : 's'} pinned
                </Text>
              )}
            </div>
          )}

          {eligibleEnvironmentIds && (
            <div className="pt-2 mt-2 border-t border-border">
              <Text size="xs" className="text-muted-foreground">
                Currently eligible for{' '}
                <Text as="span" weight="medium" size="xs">
                  {eligibleEnvironmentIds.length}
                </Text>{' '}
                environment{eligibleEnvironmentIds.length === 1 ? '' : 's'} on this property.
              </Text>
            </div>
          )}
        </div>

        {environments && environments.length > 0 && (
          <div className="space-y-1.5">
            <Select
              label="Default environment"
              items={defaultEnvironmentOptions}
              selectedKey={defaultEnvironmentId ?? '__default__'}
              onSelectionChange={(key) =>
                onDefaultEnvironmentIdChange(!key || key === '__default__' ? null : String(key))
              }
            />
            <Text size="xs" className="text-muted-foreground">
              Where this flow runs when a suite runs each flow against its preferred env (mixed
              mode). Falls back to the property default.
            </Text>
          </div>
        )}

        {passByEnvironment && passByEnvironment.length > 0 && (
          <div className="space-y-1.5 rounded-md border border-border bg-card p-3">
            <Text size="xs" weight="medium">
              Pass history by environment
            </Text>
            {passByEnvironment.map((passHistory) => {
              const allGreen =
                passHistory.everPassed &&
                passHistory.total > 0 &&
                passHistory.passed === passHistory.total;
              const sandboxReady =
                passHistory.environmentType === 'sandbox' && !allGreen ? ' (sandbox-ready?)' : '';

              return (
                <Flex key={passHistory.environmentId} align="center" gap="sm" justify="between">
                  <Text size="xs">
                    {passHistory.environmentName} ({passHistory.environmentType})
                  </Text>
                  <TagBadge
                    name={`${formatPassHistoryLabel(passHistory)}${sandboxReady}`}
                    color={allGreen ? 'blue' : 'orange'}
                    hideDot
                  />
                </Flex>
              );
            })}
          </div>
        )}
      </div>
    </SettingsSection>
  );
}
